import { useState } from "react";
import { motion } from "framer-motion";
import { MapPin, Phone, Moon, Heart } from "lucide-react";
import { useLang } from "@/i18n";
import { NIGHTLIFE, MOODS } from "@/nightlifeData";
import { useFavorites } from "@/favorites";
import { fmtDate } from "@/api";

const fadeUp = (delay = 0) => ({
  initial: { opacity: 0, y: 30 }, whileInView: { opacity: 1, y: 0 },
  viewport: { once: true, margin: "-60px" }, transition: { duration: 0.8, delay },
});

export default function AfterDark() {
  const { t, lang } = useLang();
  const [mood, setMood] = useState("all");
  const { favs, toggle } = useFavorites();
  const venues = NIGHTLIFE.filter((v) => mood === "all" || v.mood.includes(mood)).sort((a, b) => a.sort_order - b.sort_order);
  const lastChecked = NIGHTLIFE.map((v) => v.last_verified).filter(Boolean).sort().pop();

  return (
    <section id="after-dark" className="py-24 md:py-32 bg-main text-cream relative overflow-hidden grain" data-testid="after-dark">
      <div className="max-w-[1400px] mx-auto px-6 md:px-12 relative z-10">
        <div className="grid lg:grid-cols-12 gap-12 items-start">
          <div className="lg:col-span-7">
            <motion.p {...fadeUp()} className="text-xs md:text-sm tracking-[0.25em] uppercase font-bold text-sage flex items-center gap-3">
              <span className="inline-block w-10 h-px bg-terracotta" />
              {t("ad.eyebrow")}
            </motion.p>
            <motion.h2 {...fadeUp(0.1)} className="mt-5 font-serif font-medium text-4xl md:text-5xl lg:text-6xl leading-[1.05] tracking-tight">
              {t("ad.title")}
            </motion.h2>
            <motion.p {...fadeUp(0.15)} className="mt-6 text-cream/70 font-light leading-relaxed max-w-2xl">
              {t("ad.sub")}
            </motion.p>

            <motion.div {...fadeUp(0.2)} className="mt-10">
              <p className="text-xs tracking-[0.2em] uppercase font-bold text-cream/50">{t("ad.selector")}</p>
              <div className="mt-4 flex flex-wrap gap-2" data-testid="mood-selector">
                <button onClick={() => setMood("all")} data-testid="mood-all"
                  className={`rounded-full px-5 py-2.5 text-sm font-semibold transition-all duration-300 ${mood === "all" ? "bg-terracotta text-cream-light" : "bg-cream/5 border border-cream/15 text-cream/80 hover:border-terracotta/50"}`}>
                  {t("ad.all")}
                </button>
                {MOODS.map((m) => (
                  <button key={m} onClick={() => setMood(m)} data-testid={`mood-${m}`}
                    className={`rounded-full px-5 py-2.5 text-sm font-semibold transition-all duration-300 ${mood === m ? "bg-terracotta text-cream-light" : "bg-cream/5 border border-cream/15 text-cream/80 hover:border-terracotta/50"}`}>
                    {t(`ad.moods.${m}`)}
                  </button>
                ))}
              </div>
            </motion.div>

            <div className="mt-8 grid sm:grid-cols-2 gap-4">
              {venues.map((v) => (
                <motion.article key={v.id} {...fadeUp(0.1)} className="rounded-2xl border border-cream/15 bg-cream/5 p-5" data-testid={`venue-${v.id}`}>
                  <div className="flex items-start justify-between gap-2">
                    <div>
                      <p className="text-[10px] tracking-[0.2em] uppercase font-bold text-terracotta">{v[`tagline_${lang}`] || v.tagline_en || v.tagline_ro}</p>
                      <h3 className="mt-1.5 font-serif text-xl">{v.name}</h3>
                    </div>
                    <button onClick={() => toggle(v.id)} data-testid={`venue-fav-${v.id}`} aria-label={t("attx.fav_add")}
                      className={`shrink-0 w-8 h-8 rounded-full flex items-center justify-center transition-colors ${favs.includes(v.id) ? "text-terracotta" : "text-cream/40 hover:text-terracotta"}`}>
                      <Heart size={16} className={favs.includes(v.id) ? "fill-terracotta" : ""} />
                    </button>
                  </div>
                  <p className="mt-2 text-xs text-cream/70 font-light leading-relaxed">{v[`desc_${lang}`] || v.desc_en || v.desc_ro}</p>
                  {(v[`imperfect_says_${lang}`] || v.imperfect_says_en) && (
                    <p className="mt-2 font-serif italic text-sm text-sage">„{v[`imperfect_says_${lang}`] || v.imperfect_says_en}"</p>
                  )}
                  <div className="mt-3 space-y-1 text-[11px] text-cream/50">
                    {v.address && <p className="flex items-center gap-1.5"><MapPin size={11} /> {v.address}</p>}
                    {v.phone && <p className="flex items-center gap-1.5"><Phone size={11} /> {v.phone}</p>}
                  </div>
                </motion.article>
              ))}
            </div>
            <motion.p {...fadeUp(0.2)} className="mt-6 text-xs text-cream/40 font-light" data-testid="after-dark-note">
              {t("ad.note")}
              {lastChecked && <> · {t("ev.verified").replace("{d}", fmtDate(lastChecked))}</>}
            </motion.p>
          </div>

          <div className="lg:col-span-5 lg:sticky lg:top-28 space-y-6">
            <motion.div {...fadeUp(0.2)} className="overflow-hidden rounded-3xl border border-cream/15 shadow-2xl">
              <img src="/assets/brasov-after-dark.jpg" alt="Brașov After Dark — Imperfect House" loading="lazy"
                className="w-full h-auto" data-testid="after-dark-poster" />
            </motion.div>
            <motion.div {...fadeUp(0.25)} className="rounded-3xl border border-cream/15 bg-cream/5 p-7" data-testid="after-dark-solo">
              <p className="flex items-center gap-2 font-serif text-2xl"><Moon size={20} className="text-terracotta" /> {t("ad.solo_title")}</p>
              <p className="mt-3 text-sm text-cream/70 font-light leading-relaxed">{t("ad.solo_text")}</p>
              <p className="mt-4 font-serif italic text-sage">{t("ad.home")}</p>
            </motion.div>
          </div>
        </div>
      </div>
    </section>
  );
}
